import React, { useEffect, useState, useMemo } from "react"
import axios from "axios"
import { api } from "../../utils/variables"
import "./CustomInsight.css"
import { useNavigate } from "react-router-dom"
import AppRankCard from "./AppRankCard"
import AppCardSkeleton from "./AppCardSkeleton"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import PlatformTabs from "../../components/Common/PlatformTabs"
import { useFilters } from "../../hooks/useFilters"
import { ChevronUp } from "lucide-react"
import Select, { components as RSComponents } from 'react-select'
import flags from "../../common/flag"
import { Spinner } from "reactstrap"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, applySEODate } from "../../utils/seoUtils"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

// Platform pill row defaults to Meta Quest on first load (before the user has
// picked/saved a platform filter of their own).
const DEFAULT_PLATFORM = ["meta"]

export default function TopRated() {
  const { trackEvent } = useAnalytics()
  const { filterSections } = useFilters()
  const [topFilterPanelOpen, setTopFilterPanelOpen] = useState(false)
  const [rankSort, setRankSort] = useState(null)
  const [hoverRank, setHoverRank] = useState(false)

  const parseRank = (item) => {
    if (!item) return Number.MAX_SAFE_INTEGER
    const candidates = [item.rank, item.position, item.ranking, item.rank_position, item.rankValue]
    for (let c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    if (item.meta && item.meta.rank !== undefined) return Number(item.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const getDisplayRank = (item, idx) => {
    const r = parseRank(item)
    return r === Number.MAX_SAFE_INTEGER ? (idx + 1) : r
  }

  const sortByRank = (order = 'asc') => {
    const arr = Array.isArray(topRated) ? [...topRated] : []
    arr.sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return order === 'asc' ? ra - rb : rb - ra
    })
    setSortedTopRated(arr)
    setIsSorted(true)
  }

  const toggleRankSort = () => {
    const next = rankSort === 'asc' ? 'desc' : 'asc'
    setRankSort(next)
    sortByRank(next)
    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_SORT_CHANGED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_AVERAGE_RATING, direction: next })
  }
  const [platform, changePlatform] = useState("all")
  const [topRated, setTopRated] = useState([])
  const [topRatedDetails, setTopRatedDetails] = useState([])
  const [sortedTopRated, setSortedTopRated] = useState([])
  const [isSorted, setIsSorted] = useState(false) // Track sorting state
  const [itemsToShow, setItemsToShow] = useState(6) // Initially show 6 items
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(false)
  const isReleasedMeta = sessionStorage.getItem("releaseMeta")
  const navigate = useNavigate()

  const [topCountry, setTopCountry] = useState(() => {
    try {
      return sessionStorage.getItem('topRated_selectedCountry') || 'US'
    } catch (e) {
      return 'US'
    }
  })
  const [topLanguage, setTopLanguage] = useState(() => {
    try {
      return sessionStorage.getItem('topRated_selectedLanguage')
    } catch (e) {
      return null
    }
  })
  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)

  const [topRatedLastUpdated, setTopRatedLastUpdated] = useState(null)
  const [topAppliedFilters, setTopAppliedFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem('topRatedAppliedFilters')
      const saved = raw ? JSON.parse(raw) : {}
      // Platform must not persist across a refresh or navigating away and back —
      // always reset to the Meta Quest default instead of restoring the last pick.
      return { ...saved, platform: DEFAULT_PLATFORM }
    } catch (e) {
      return { platform: DEFAULT_PLATFORM }
    }
  })
  const [topFromDate, setTopFromDate] = useState(() => {
    try {
      const stored = sessionStorage.getItem('topRated_fromDate')
      if (stored) return stored
    } catch (e) {
      // ignore
    }
    const today = new Date()
    const prior = new Date(today)
    prior.setDate(today.getDate() - 7)
    const iso = prior.toISOString().slice(0, 10)
    try { sessionStorage.setItem('topRated_fromDate', iso) } catch (e) { }
    return iso
  })
  const [topToDate, setTopToDate] = useState(() => {
    try {
      const stored = sessionStorage.getItem('topRated_toDate')
      if (stored) return stored
    } catch (e) {
      // ignore
    }
    const today = new Date()
    const iso = today.toISOString().slice(0, 10)
    try { sessionStorage.setItem('topRated_toDate', iso) } catch (e) { }
    return iso
  })

  const handleCardClick = (app) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_APP_SELECTED, { app_id: app?.item_id, app_name: app?.name })
    navigate(`/xr/app/${(app?.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${app?.item_id}`, {
      state: { app_id: app?.item_id, data: app, source_surface: "custom_insight_top_rated" }
    })
  }

  useEffect(() => {
    setLoading(true)
    fetchAll()
  }, [])

  const fetchAll = () => {
    try {
      fetchTopRated()
    } finally {
    }
  }

  const fetchTopRated = async (overrides = {}, filters = topAppliedFilters) => {
    try {
      const params = {}
      const country = overrides.country !== undefined ? overrides.country : topCountry
      const language = overrides.language !== undefined ? overrides.language : topLanguage
      const fromDate = overrides.from_date !== undefined ? overrides.from_date : topFromDate
      const toDate = overrides.to_date !== undefined ? overrides.to_date : topToDate
      if (country && country !== 'US') params.country = country
      if (language) params.language = language
      if (fromDate) params.from_date = fromDate
      if (toDate) params.to_date = toDate
      // Add filter params
      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.platform) params.platform = Array.isArray(filters.platform) ? filters.platform.join(',') : filters.platform
        if (filters.sort_by) params.sort_by = filters.sort_by
        else if (filters.sort) params.sort_by = filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
        if (filters.revenue) params.revenue = Array.isArray(filters.revenue) ? filters.revenue.join(',') : filters.revenue
        if (filters.release_date) params.release_date = Array.isArray(filters.release_date) ? filters.release_date.join(',') : filters.release_date
      }
      const token = localStorage.getItem("authToken")
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const res = await axios.get(`${api}/tracker/dashboard/top_rated`, { params, headers })
      const raw = Array.isArray(res.data?.data) ? res.data.data : []
      const withRank = raw.map((item, index) => ({
        ...item,
        rank: item?.rank !== undefined && item?.rank !== null && item?.rank !== ''
          ? item.rank
          : (item?.position !== undefined && item?.position !== null && item?.position !== ''
            ? item.position
            : index + 1)
      }))
      setTopRated(withRank)
      setTopRatedDetails(res.data.details)
      trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_VIEWED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_AVERAGE_RATING, app_count: withRank.length })
      setTopRatedLastUpdated(new Date())
    } catch (error) {
      console.error("Error fetching in Top rated:", error)
    } finally {
      setLoading(false)
    }
  }

  // Load countries for the dropdowns
  useEffect(() => {
    let mounted = true
    const loadCountries = async () => {
      setCountriesLoading(true)
      try {
        const res = await axios.get(`${api}/countries/`)
        const list = res.data?.data || []
        if (!mounted) return
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))

        const entry = list.find(x => x.code === topCountry)
        const defaultLocale = entry && Array.isArray(entry.locales) && entry.locales.length > 0 ? entry.locales[0] : null
        setTopLanguage(prev => prev || defaultLocale)
      } catch (err) {
        console.error('Error loading countries:', err)
      } finally {
        if (mounted) setCountriesLoading(false)
      }
    }
    loadCountries()
    return () => { mounted = false }
  }, [])

  const getLocalesForCountry = (countryCode) => {
    if (!countriesList || countriesList.length === 0) return []
    const c = countriesList.find(x => x.code === countryCode)
    if (!c || !Array.isArray(c.locales)) return []
    return c.locales.map(l => ({ value: l, label: (l || '').replace(/_/g, ' ').replace(/(^|\s)\S/g, s => s.toUpperCase()) }))
  }

  const CountryOption = (props) => {
    const { innerRef, innerProps, data, isFocused, isSelected } = props
    const FlagComp = flags[data.value]
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: isFocused ? '#3A3B47' : 'transparent', color: isSelected ? '#F6255A' : '#fff', cursor: 'pointer' }}
      >
        {FlagComp ? <FlagComp style={{ width: 20, height: 'auto', marginRight: 8 }} /> : null}
        <span>{data.label}</span>
      </div>
    )
  }

  const SingleValue = (props) => {
    const { data } = props
    const FlagComp = flags[data.value]
    return (
      <RSComponents.SingleValue {...props}>
        {FlagComp ? <FlagComp style={{ width: 18, height: 'auto', marginRight: 8 }} /> : null}
        {data.label}
      </RSComponents.SingleValue>
    )
  }

  const selectControlStyles = {
    control: (provided) => ({ ...provided, background: '#171821', border: '1px solid rgba(53, 55, 72, 1)', minHeight: 32, height: 32, borderRadius: 6, paddingLeft: 8, color: 'white', fontSize: 14 }),
    singleValue: (provided) => ({ ...provided, color: '#fff', fontSize: 14 }),
    menu: (provided) => ({ ...provided, background: '#171821', color: '#fff' }),
    option: (provided, state) => ({ ...provided, background: state.isFocused ? '#3A3B47' : 'transparent', color: state.isSelected ? '#F6255A' : '#fff' }),
  }

  const handlePlatformChange = platform => {
    changePlatform(platform) // Update the platform

    const platformReleaseGames =
      platform !== "all"
        ? topRated.filter(game => game.platform === platform)
        : topRated // If "all", return all games
    setSortedTopRated(platformReleaseGames)
  }

  const handleSort = event => {
    const sortBy = event.target.value
    let sortedRelease = []
    if (sortBy === "revenue") {
      sortedRelease = [...topRated].sort((a, b) => b.revenue - a.revenue)
    } else if (sortBy === "downloads") {
      sortedRelease = [...topRated].sort((a, b) => b.downloads - a.downloads)
    } else {
      sortedRelease = topRated
    }
    setSortedTopRated(sortedRelease)
    setIsSorted(true)
  }

  const handleFilterApply = (filters) => {
    // Normalize filters: convert 'sort' to 'sort_by' (FilterPanel uses 'sort' as section id)
    const normalizedFilters = { ...(filters || {}) }
    // Accept either `sort_by` (preferred) or legacy `sort` and normalize to sort_by
    if (normalizedFilters.sort_by) {
      // Already has sort_by, keep it
    } else if (normalizedFilters.sort) {
      normalizedFilters.sort_by = normalizedFilters.sort
      delete normalizedFilters.sort
    }

    setTopAppliedFilters(normalizedFilters)
    setLoading(true)
    // persist filters so they survive route changes
    try { sessionStorage.setItem('topRatedAppliedFilters', JSON.stringify(normalizedFilters || {})) } catch (e) { /* ignore */ }
    fetchTopRated({}, normalizedFilters)
    setTopFilterPanelOpen(false)
  }

  // Single-select platform pill row next to the title — picking one platform
  // replaces whatever was selected before (only one active at a time); clicking
  // the already-active platform clears the filter back to "all".
  const handlePlatformToggle = (platformId) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PLATFORM_CHANGED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_AVERAGE_RATING, platform: platformId })
    const current = Array.isArray(topAppliedFilters?.platform) ? topAppliedFilters.platform : []
    const isAlreadyActive = current.length === 1 && current[0] === platformId
    const next = isAlreadyActive ? [] : [platformId]
    const newFilters = { ...(topAppliedFilters || {}) }
    if (next.length) newFilters.platform = next
    else delete newFilters.platform
    setTopAppliedFilters(newFilters)
    setLoading(true)
    try { sessionStorage.setItem('topRatedAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
    fetchTopRated({}, newFilters)
  }

  const handleRemoveFilter = (key) => {
    const newFilters = { ...(topAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setTopAppliedFilters(newFilters)
      // re-fetch with the updated filters
      setLoading(true)
      try { sessionStorage.setItem('topRatedAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      fetchTopRated({}, newFilters)
    }
  }

  useEffect(() => {
    if (showAll) setItemsToShow(topRated.length)
    else setItemsToShow(6)
  }, [showAll]) // Re-run when "showAll" changes or data length changes

  const slides = isSorted
    ? sortedTopRated.slice(0, itemsToShow)
    : topRated.slice(0, itemsToShow)

  const formatNumber = num => {
    if (num >= 1e6) {
      return (num / 1e6).toFixed(1) + " M"
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + " K"
    }
    return num || 0
  }

  const formatRating = (ratingObj) => {
    const avg = ratingObj && typeof ratingObj.average === 'number' ? ratingObj.average : null
    if (avg === null) return "N/A"
    return avg.toFixed(1)
  }

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.topRated.title)}
        description={PAGE_SEO.topRated.description}
        keywords={PAGE_SEO.topRated.keywords}
        canonical={generateCanonicalUrl('/top-rated')}
        robots={PAGE_SEO.topRated.robots}
        pageType="topRated"
      />
      <div className="custom-insight-page-content top-improved-container gross-page">
        <div className="top-header-section">
          <div className="title-area" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <h1 className="top-improved-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              Top Rated Apps
            </h1>
            {/* Platform pills next to the title — desktop only; on smaller screens
                they drop to their own row below (see the d-lg-none block). */}
            <div className="d-none d-lg-flex">
              <PlatformTabs
                variant="pill"
                showDivider={false}
                selected={Array.isArray(topAppliedFilters?.platform) ? topAppliedFilters.platform : []}
                onToggle={handlePlatformToggle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginLeft: 'auto' }}>
            <div>
              <button
                type="button"
                className="filter-trigger"
                onClick={() => setTopFilterPanelOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  height: 34,
                  borderRadius: 8,
                  background: '#171821',
                  border: '1px solid #F6255A',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 400,
                  position: 'relative'
                }}
              >
                <i className="fa fa-filter" style={{ fontSize: '0.75rem' }} />
                <span style={{ marginLeft: 6 }}>Filters</span>
                {/* `platform` is excluded here — it's the pill row above/below, not a
                    modal filter, so it shouldn't bump this badge. */}
                {topAppliedFilters && Object.keys(topAppliedFilters).filter(k => k !== "platform").length > 0 && (
                  <span className="filter-badge">
                    {Object.entries(topAppliedFilters).reduce((acc, [key, v]) => {
                      if (key === "platform") return acc;
                      if (v == null) return acc;
                      if (Array.isArray(v)) return acc + v.length;
                      if (typeof v === "string" || typeof v === "number") return acc + (String(v).trim() ? 1 : 0);
                      return acc + 1
                    }, 0)}
                  </span>
                )}
              </button>
            </div>

            <div
              role="button"
              aria-label="Sort by rank"
              title={rankSort ? `Sort by rank (${rankSort === 'asc' ? 'Ascending' : 'Descending'})` : 'Sort by rank (toggle asc/desc)'}
              onClick={(e) => { e.stopPropagation(); toggleRankSort(); }}
              onMouseEnter={() => setHoverRank(true)}
              onMouseLeave={() => setHoverRank(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                padding: '6px 10px',
                height: 34,
                borderRadius: 8,
                background: rankSort ? 'rgba(232,62,140,0.06)' : (hoverRank ? 'rgba(255,111,163,0.06)' : 'transparent'),
                border: '1px solid #F6255A',
                transition: 'all 150ms ease',
                boxShadow: hoverRank ? '0 8px 20px rgba(232,62,140,0.12)' : 'none'
              }}
            >
              <span style={{ fontSize: '14px', color: 'white' }}>Sort by</span>
              <ChevronUp
                size={18}
                color={rankSort ? '#e83e8c' : (hoverRank ? '#ff6fa3' : '#A1ACB8')}
                style={{
                  transform: rankSort === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 160ms ease, color 160ms ease'
                }}
              />
            </div>
          </div>
        </div>

        {/* Same platform pills, shown on their own row below the title on
            mobile/tablet where there isn't room for them next to it. */}
        <div className="d-lg-none" style={{ marginBottom: "10px" }}>
          <PlatformTabs
            variant="pill"
            showDivider={false}
            disabledPlatforms={["apple_vision"]}
            selected={Array.isArray(topAppliedFilters?.platform) ? topAppliedFilters.platform : []}
            onToggle={handlePlatformToggle}
          />
        </div>

        <div className="top-improved-description-section">
          <p className="top-improved-description">
            Apps with the highest average user ratings across the selected time period.
          </p>
          {/* Applied filter badges (mirror Trending page) */}
          {Object.keys(topAppliedFilters || {}).filter(k => k !== "platform").length > 0 && (
            <div style={{ marginTop: '4px', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(topAppliedFilters).map(([key, value]) => {
                if (key === "platform") return null
                const displayVal = Array.isArray(value) ? value.join(', ') : (value === null || value === undefined || value === '' ? 'Any' : String(value))
                return (
                  <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                    <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                    <span style={{ opacity: 0.95, color: '#F6255A' }}>{displayVal}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleRemoveFilter(key)
                      }}
                      aria-label={`Remove filter ${key}`}
                      style={{ marginLeft: 4, background: 'transparent', border: 'none', color: '#F6255A', cursor: 'pointer', fontSize: 12, lineHeight: 0.9 }}
                    >
                      ×
                    </button>
                  </span>
                )
              })}
            </div>
          )}
          {/* Date range picker (From at left end, To + Apply at right end) */}
          {/* <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160, marginRight: 12 }}>
              <label style={{ fontSize: 12, color: '#A1ACB8', marginBottom: 6 }}>From</label>
              <input
                type="date"
                value={topFromDate || ''}
                onChange={(e) => {
                  const v = e.target.value
                  setTopFromDate(v)
                  try { sessionStorage.setItem('topRated_fromDate', v || '') } catch (err) { }
                }}
                style={{ height: 34, borderRadius: 6, padding: '6px 8px', background: '#171821', border: '1px solid rgba(53,55,72,1)', color: '#fff' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
                <label style={{ fontSize: 12, color: '#A1ACB8', marginBottom: 6 }}>To</label>
                <input
                  type="date"
                  value={topToDate || ''}
                  onChange={(e) => {
                    const v = e.target.value
                    setTopToDate(v)
                    try { sessionStorage.setItem('topRated_toDate', v || '') } catch (err) { }
                  }}
                  style={{ height: 34, borderRadius: 6, padding: '6px 8px', background: '#171821', border: '1px solid rgba(53,55,72,1)', color: '#fff' }}
                />
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => { setLoading(true); fetchTopRated() }}
                  style={{ padding: '8px 12px', borderRadius: 8, background: '#F6255A', color: '#fff', border: 'none', cursor: 'pointer' }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div> */}
        </div>



        <div className="app-cards-container">
          {loading ? (
            <AppCardSkeleton count={6} />
          ) : topRated.length === 0 ? (
            // ✅ Show "No Data" Message
            <div className="no-data-container">
              <p className="no-data-message">No data available</p>
            </div>
          ) : (
            // ✅ Normal Cards
            <>
              {slides.map((app, idx) => {
                const fallbackPlatformId = Array.isArray(topAppliedFilters?.platform) && topAppliedFilters.platform.length === 1 ? topAppliedFilters.platform[0] : null
                return (
                  <AppRankCard
                    key={app?.item_id || idx}
                    app={app}
                    rank={getDisplayRank(app, idx)}
                    fallbackPlatformId={fallbackPlatformId}
                    onClick={() => handleCardClick(app)}
                    secondMetricLabel="Average Rating"
                    secondMetricValue={<span className="metric-value">{formatRating(app?.rating)}</span>}
                  />
                )
              })}
            </>
          )}
        </div>

        {!loading && !showAll && topRated.length > 6 && (
          <div className="see-more-container">
            <button
              className="see-more-button"
              onClick={() => { trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_LIST_EXPANDED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_AVERAGE_RATING, expanded: true }); setShowAll(true) }}
            >
              See More
            </button>
          </div>
        )}

        {!loading && showAll && topRated.length > 6 && (
          <div className="see-more-container">
            <button
              className="see-more-button"
              onClick={() => { trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_LIST_EXPANDED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_AVERAGE_RATING, expanded: false }); setShowAll(false) }}
            >
              See Less
            </button>
          </div>
        )}

        <FilterPanel
          open={topFilterPanelOpen}
          hidePlatformSection
          sections={filterSections.length > 0 ? filterSections : undefined}
          onClose={() => setTopFilterPanelOpen(false)}
          onApply={handleFilterApply}
          initialValues={useMemo(() => {
            // Convert sort_by back to sort for FilterPanel (FilterPanel uses 'sort' as section id)
            const panelFilters = { ...(topAppliedFilters || {}) }
            if (panelFilters.sort_by && !panelFilters.sort) {
              panelFilters.sort = panelFilters.sort_by
              delete panelFilters.sort_by
            }
            return panelFilters
          }, [topAppliedFilters])}
        />
      </div>
    </React.Fragment>
  )
}
